import type { JSX } from 'react'
import type { RoomView, SessionView } from '../../api/rooms'
import { sessions as sessionsApi } from '../../api/rooms'

export type RoomWaitingProps = {
  view: RoomView
  session: SessionView
  isAdmin: boolean
  /** Свести досрочно, не дожидаясь отставших. Только у ведущего. */
  onFinish: () => void
  onBack?: () => void
}

/**
 * Экран между записью и результатом: видно, кого ждём, а когда всё готово —
 * само сведённое видео.
 */
export function RoomWaiting({ view, session, isAdmin, onFinish, onBack }: RoomWaitingProps): JSX.Element {
  const state = session.session.state
  const waiting = session.progress.filter((p) => p.total > 0 && p.recorded < p.total)

  if (state === 'done') {
    return (
      <div className="page room-result">
        <h1>Готово</h1>
        <p className="faint">{session.game.game.title}</p>
        <video src={sessionsApi.resultUrl(session.session.id)} controls autoPlay className="result-video" />
        <div className="row">
          <a className="button" href={sessionsApi.resultUrl(session.session.id)} download>
            Скачать
          </a>
          {isAdmin && onBack ? (
            <button type="button" className="primary" onClick={onBack}>
              Вернуться в комнату
            </button>
          ) : (
            <p className="faint">Ведущий может выбрать следующую сцену.</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="page room-waiting">
      <h1>{state === 'rendering' ? 'Сводим…' : 'Ждём остальных'}</h1>
      <p className="faint">
        {state === 'rendering'
          ? 'Собираем все голоса в одно видео. Это занимает несколько секунд.'
          : 'Свои реплики вы записали. Осталось дождаться остальных.'}
      </p>

      <ul className="progress-list">
        {session.progress
          .filter((p) => p.total > 0)
          .map((p) => {
            const done = p.recorded >= p.total
            return (
              <li key={p.participantId} className={done ? 'done' : ''}>
                <span>{p.name}</span>
                <span className="spacer" />
                <span className="mono">
                  {p.recorded} / {p.total}
                </span>
                <span className="progress-bar">
                  <span style={{ width: `${p.total ? (100 * p.recorded) / p.total : 0}%` }} />
                </span>
              </li>
            )
          })}
      </ul>

      {session.progress.every((p) => p.total === 0) ? (
        <p className="faint">Вам не досталось ролей в этой сцене — просто дождитесь результата.</p>
      ) : null}

      {isAdmin && state === 'recording' && waiting.length > 0 ? (
        <button type="button" className="ghost" onClick={onFinish}>
          Свести без ожидания ({waiting.map((p) => p.name).join(', ')})
        </button>
      ) : null}

      {session.session.error ? <p className="error-text">{session.session.error}</p> : null}
      <p className="faint">
        Комната <span className="mono">{view.room.code}</span>
      </p>
    </div>
  )
}
